/**
 * PATH: packages/email-hub/src/feature/components/EntityPeekShell.tsx
 * FILE_ID: EMAILHUB-ENTITY-PEEK-SHELL-001
 * PURPOSE: Shared right slide-over chrome for "peek-first" entity panels
 *          (job / board) opened from the email reader's chips. Click a
 *          chip -> peek over the inbox; the footer's "Open" button does
 *          the full navigation. Mirrors CardPeekDrawer's fixed-overlay
 *          pattern so it works from anywhere in the tree.
 * WHY:     Triage v5 decision (operator 2026-07-07, canvas-locked):
 *          mid-triage chip clicks are mostly VERIFICATION; a peek answers
 *          those without ejecting the operator from the inbox, and going
 *          to work the entity costs exactly one more click.
 * FLOW:
 *   ┌───────────┐   ┌──────────────────┐   ┌───────────────────┐
 *   │ chip click│ → │ overlay + panel  │ → │ Open → full nav    │
 *   │ (host)    │   │ (children slot)  │   │ Esc/scrim → close  │
 *   └───────────┘   └──────────────────┘   └───────────────────┘
 * RELATED:
 *   - ./JobPeekPanel.tsx, ./BoardPeekPanel.tsx (the two consumers)
 *   - @quotech/kanban-host CardPeekDrawer (the card twin; z-[55] parity)
 */

import { useEffect, type ReactNode } from 'react'
import { ArrowUpRight, X } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'

export interface EntityPeekShellProps {
  /** Panel heading (entity name). */
  title: ReactNode
  /** Small line under the heading (e.g. "Job" / "Kanban board"). */
  subtitle?: ReactNode
  /** Leading glyph next to the title. */
  icon?: ReactNode
  /** Close the peek (scrim click / Esc / X). */
  onClose: () => void
  /** Full navigation to the entity; renders the footer "Open" button. */
  onOpen?: () => void
  /** Footer button label, defaults to "Open". */
  openLabel?: string
  children: ReactNode
}

/**
 * I→O: (title, children, onClose/onOpen) → a fixed right slide-over.
 * z-[55] matches CardDetailDrawer so a peek never fights the card drawer.
 */
export function EntityPeekShell({
  title,
  subtitle,
  icon,
  onClose,
  onOpen,
  openLabel = 'Open',
  children,
}: EntityPeekShellProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[55] flex justify-stretch md:justify-end bg-black/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="entity-peek"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          'h-full w-full md:max-w-sm bg-background border-l border-border shadow-xl',
          'flex flex-col',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2.5 px-4 py-3 border-b border-border">
          {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">{title}</div>
            {subtitle && (
              <div className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close peek"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>

        {onOpen && (
          <div className="px-4 py-3 border-t border-border">
            <button
              type="button"
              onClick={onOpen}
              data-testid="entity-peek-open"
              className={cn(
                'w-full inline-flex items-center justify-center gap-1.5 rounded-md',
                'bg-primary text-primary-foreground text-sm font-medium px-3 py-2',
                'hover:bg-primary/90 transition-colors',
              )}
            >
              {openLabel}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EntityPeekShell
